import { useEffect, useRef } from "react";
import { useAtomValue } from "jotai";

import { accessKeyAtom } from "@/atoms/auth.atoms";
import { participantIdAtom, romIdAtom } from "@/atoms/rom.atoms";
import { HttpError } from "@/services/api";
import { romService } from "@/services/rom.service";

const HEARTBEAT_INTERVAL_MS = 30_000;

/**
 * Sends a heartbeat for the current rom participant every 30 seconds while
 * we have an active session (accessKey + romId + participantId).
 *
 * Backend may respond with 410 Gone if the participant has left or the rom
 * has been closed by the scheduler / other peer — we dispatch `rom:closed`
 * which triggers a redirect to /connection-mode but keeps the user logged in.
 * Genuine auth failures (401) are handled separately via `auth:session-expired`.
 */
export function useRomHeartbeat() {
  const accessKey = useAtomValue(accessKeyAtom);
  const romId = useAtomValue(romIdAtom);
  const participantId = useAtomValue(participantIdAtom);

  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    if (!accessKey || !romId || !participantId) {
      return;
    }

    let cancelled = false;

    const tick = async () => {
      try {
        await romService.heartbeat(romId, participantId);
      } catch (err) {
        if (cancelled) return;
        if (
          err instanceof HttpError &&
          (err.status === 410 || err.status === 404)
        ) {
          window.dispatchEvent(new CustomEvent("rom:closed"));
          return;
        }
        // Network / transient → retry on next interval
        console.warn("[useRomHeartbeat] tick failed:", err);
      }
    };

    // fire immediately so the first heartbeat doesn't wait 30s
    void tick();

    timerRef.current = setInterval(tick, HEARTBEAT_INTERVAL_MS);

    return () => {
      cancelled = true;
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [accessKey, romId, participantId]);
}
